import dynamic from "next/dynamic"
import { useRouter } from "next/router"
import { MainLayout } from "components/Layout"
import { useLang } from "hooks/useLang"
import { EventJsonLd, NextSeo, BreadcrumbJsonLd } from "next-seo"
import { useEffect, useMemo, useState, useRef } from "react"
import { IFinalEvent } from "types/event"
import useWindowSize from "hooks/useWindowSize"
import removeMarkdown from "utils/helpers/removeMarkdown"
import { useAnalytics } from "components/Analytics/UnifiedAnalyticsProvider"
import { useAnalyticsTracking } from "hooks/useAnalyticsTracking"
import { ITicket } from "types/ticket"
import axios from "axios"
import { useMutation } from "@tanstack/react-query"
import PrivateEventModal from "components/Modal/PrivateEventModal"
import BlurView from "../../../components/EventDetailsPage/components/blurview"
import EventLayout from "../../../components/EventDetailsPage/components/layout"
import Image from "next/image"
import dayjs from "dayjs"
import LocationIcon from "components/icons/LocationIcon"
import DateIcon from "components/icons/DateIcon"
import EventDescription from "components/EventDetailsPage/EventDescription"

import BuyBtn from "../../../components/EventDetailsPage/components/buyBtn"
import ShareAndLike from "components/EventDetailsPage/ShareAndLike"
import EventSkeleton from "components/ActivityLoader/activityLoader"
import CustomMarkdown from "components/atoms/CustomMarkdown"
import AuthModal from "components/bookingv2/AuthModal/AuthModal"
import Link from "next/link"
import {
    getCanonicalUrl,
    getEventPageServerSideProps,
    getLanguageAlternates,
} from "utils/eventPage"
import { useEventPageQuery } from "hooks/useEventPage"
import {
    useEventDetailsLifecycle,
    useStickyBuyButton,
} from "hooks/useEventDetailsPage"

// Lazy load heavy components to reduce initial bundle size
const LiveMap = dynamic(() => import("../../../components/LiveMap/liveMap"), {
    ssr: false,
    loading: () => (
        <div className="w-full h-64 bg-gray-800 animate-pulse rounded-lg" />
    ),
})

const EventGallery = dynamic(
    () => import("components/EventDetailsPage/EventGallery"),
    {
        loading: () => (
            <div className="w-full h-48 bg-gray-800 animate-pulse rounded-lg" />
        ),
    }
)

const RenderVideo = dynamic(() => import("components/video/renderVideo"), {
    ssr: false,
    loading: () => (
        <div className="w-full aspect-video bg-gray-800 animate-pulse rounded-lg" />
    ),
})

const EventTerms = dynamic(() => import("components/bookingv2/EventTerms"), {
    loading: () => (
        <div className="w-full h-32 bg-gray-800 animate-pulse rounded-lg" />
    ),
})

export const meta = {
    author: "StartHubIT",
}

export async function getServerSideProps(context: {
    req: {
        headers: {
            origin?: string
            token?: string
        }
    }
    params?: {
        eventid?: string
    }
}) {
    return getEventPageServerSideProps({
        req: context.req,
        params: context.params,
    })
}

export type FetchEventResponse = {
    message: string
    payload: {
        event: IFinalEvent | Event
        tickets: ITicket[]
    }
}

export default function NewEventDetailsPage() {
    const { isDesktop } = useWindowSize()
    const { t } = useLang()
    const { isAr } = useLang()
    const { query, push } = useRouter()
    const router = useRouter()
    const [showAuthModal, setShowAuthModal] = useState(false)
    const contentRef = useRef<HTMLDivElement>(null)
    // const [enteredCode, setEnteredCode] = useState("");
    const [errorMessage, setErrorMessage] = useState("")
    const [isAuthorized, setIsAuthorized] = useState(false)
    const { error, isLoading, event } = useEventPageQuery()
    const { showBuy, isLockedAtFooter } = useStickyBuyButton(contentRef)

    const pageVisitSource = router.query.source as string
    const { trackEvent } = useAnalytics()
    const { sendEventViewed } = useAnalyticsTracking()
    useEventDetailsLifecycle({
        event,
        pageVisitSource,
        sendEventViewed,
        trackEvent,
    })

    const getDesktopPoster = (ev: IFinalEvent) => {
        const posterPath =
            ev?.media?.posters?.web_poster?.original ||
            ev?.poster ||
            ev?.posters?.[0] ||
            ""
        const siteUrl = (
            process.env.NEXT_PUBLIC_SITE_URL || "https://pass-tickets.com"
        ).replace(/\/$/, "")
        const defaultImageUrl = `${siteUrl}/svg/v2/pass_ticket_fallback_image_light.png`
        if (!posterPath) return defaultImageUrl
        if (/^https?:\/\//i.test(posterPath)) return posterPath
        return `${process.env.NEXT_PUBLIC_Event_Img_URL}${posterPath}`
    }

    const getMobilePoster = (ev: IFinalEvent) => {
        const posterPath =
            ev?.media?.posters?.mobile_poster?.original ||
            ev?.poster ||
            ev?.posters?.[0] ||
            ""
        const siteUrl = (
            process.env.NEXT_PUBLIC_SITE_URL || "https://pass-tickets.com"
        ).replace(/\/$/, "")
        const defaultImageUrl = `${siteUrl}/svg/v2/pass_ticket_fallback_image_light.png`
        if (!posterPath) return defaultImageUrl
        if (/^https?:\/\//i.test(posterPath)) return posterPath
        return `${process.env.NEXT_PUBLIC_Event_Img_URL}${posterPath}`
    }

    const getArtistPhoto = (photo: string) => {
        if (!photo) return ""
        if (/^https?:\/\//i.test(photo)) return photo
        return `${process.env.NEXT_PUBLIC_Event_Img_URL}${photo}`
    }

    const verifyCodeMutation = useMutation({
        mutationFn: async (code: string) => {
            const res = await axios.post(
                `${process.env.NEXT_PUBLIC_API_URL}coupons/verify`,
                {
                    event_id: event?._id,
                    coupon_code: code,
                }
            )
            return res.data
        },
        onSuccess: (data) => {
            if (data.payload) {
                setIsAuthorized(true)
                setErrorMessage("")
            } else {
                setErrorMessage("❌ Invalid code, please try again.")
            }
        },
        onError: () => {
            setErrorMessage("⚠️ Something went wrong. Try again later.")
        },
    })

    // const handleVerifyCode = () => {
    //   if (!enteredCode) {
    //     setErrorMessage("Code is required.");
    //     return;
    //   }
    //   verifyCodeMutation.mutate(enteredCode);
    // };

    useEffect(() => {
        if (query.shared) {
            trackEvent(
                "view_item",
                {
                    category: "event",
                    label: `share_event_${event?._id || ""}`,
                    value: `${query.shared}`,
                }
            )
        }
    }, [])

    if (error) {
        console.error("Error loading event data:", error)
        return (
            <MainLayout>
                <div className="flex flex-col items-center justify-center h-screen">
                    <h2 className="text-xl mb-4">Failed to load event data</h2>
                    <button
                        className="bg-mint-green-500 hover:bg-mint-green-600 text-white px-6 py-2 rounded-full"
                        onClick={() => window.location.reload()}
                    >
                        Retry
                    </button>
                </div>
            </MainLayout>
        )
    }

    // // ✅ If event is private & user not authorized, block content
    if (event?.is_private && !isAuthorized) {
        return (
            <PrivateEventModal
                open={event?.is_private && !isAuthorized}
                onClose={() => push("/")}
                onVerify={(code) => verifyCodeMutation.mutate(code)}
                isLoading={verifyCodeMutation.isPending}
                errorMessage={errorMessage}
            />
        )
    }

    if (isLoading) {
        return <EventSkeleton />
    }

    if (!event) {
        return null
    }

    const updatedEvent = event
    const mediaVideos = useMemo(
        () =>
            updatedEvent.media?.videos?.length > 0
                ? updatedEvent.media.videos
                : updatedEvent.videos || [],
        [updatedEvent.media?.videos, updatedEvent.videos]
    )
    const mediaStories = useMemo(
        () => updatedEvent.media?.stories || [],
        [updatedEvent.media?.stories]
    )
    const bgURLDesktop = useMemo(
        () => getDesktopPoster(updatedEvent),
        [updatedEvent]
    )
    const bgURLMobile = useMemo(
        () => getMobilePoster(updatedEvent),
        [updatedEvent]
    )
    const pageTitle = useMemo(
        () =>
            `${isAr ? updatedEvent.ar_title || "" : updatedEvent.en_title || ""}`,
        [isAr, updatedEvent.ar_title, updatedEvent.en_title]
    )
    const eventTitle = useMemo(
        () => (isAr ? updatedEvent.ar_title : updatedEvent.en_title) || "",
        [isAr, updatedEvent.ar_title, updatedEvent.en_title]
    )
    const description = useMemo(
        () =>
            (isAr ? updatedEvent.ar_description : updatedEvent.en_description) ||
            eventTitle,
        [isAr, updatedEvent.ar_description, updatedEvent.en_description, eventTitle]
    )
    const cleanDescription = useMemo(
        () => removeMarkdown(description || ""),
        [description]
    )
    const pageDescription = useMemo(
        () => cleanDescription.slice(0, 160),
        [cleanDescription]
    )
    const shortDescription = useMemo(
        () => cleanDescription.slice(0, 100),
        [cleanDescription]
    )
    const baseUrl = useMemo(
        () =>
            (
                process.env.NEXT_PUBLIC_SITE_URL || "https://pass-tickets.com"
            ).replace(/\/$/, ""),
        []
    )
    const canonicalUrl = useMemo(
        () => getCanonicalUrl(router.asPath),
        [router.asPath]
    )
    const imageUrl = useMemo(
        () => getDesktopPoster(updatedEvent),
        [updatedEvent]
    )
    const currentPath = useMemo(
        () => router.asPath.split("#")[0].split("?")[0] || "/",
        [router.asPath]
    )
    const localePrefix = useMemo(
        () => currentPath.match(/^\/(en-US|ar-SA)(?=\/|$)/)?.[0] || "",
        [currentPath]
    )

    const startDate = useMemo(
        () => {
            if (!updatedEvent.event_date.starting_date) return ""
            return new Date(updatedEvent.event_date.starting_date).toISOString()
        },
        [updatedEvent.event_date.starting_date]
    )
    const endDate = useMemo(
        () => {
            if (!updatedEvent.event_date.ending_date) return ""
            return new Date(updatedEvent.event_date.ending_date).toISOString()
        },
        [updatedEvent.event_date.ending_date]
    )

    const keywords = useMemo(
        () =>
            `${updatedEvent.en_title}, ${updatedEvent.ar_title}, 
    tickets, events, Saudi Arabia, ${isAr ? "باس تيكيتس" : "PassTickets"}`,
        [updatedEvent.en_title, updatedEvent.ar_title, isAr]
    )

    const eventDate = useMemo(
        () => event?.upcoming_time ?? event?.event_date?.starting_date,
        [event?.upcoming_time, event?.event_date?.starting_date]
    )
    return (
        <>
            <NextSeo
                title={pageTitle}
                description={pageDescription}
                canonical={canonicalUrl}
                languageAlternates={getLanguageAlternates(router.asPath)}
                openGraph={{
                    type: "website",
                    locale: isAr ? "ar_SA" : "en_US",
                    url: canonicalUrl,
                    title: eventTitle,
                    description: pageDescription,
                    site_name: isAr ? "باس تيكيتس" : "PassTickets",
                    images: [
                        {
                            url: imageUrl,
                            width: 1200,
                            height: 630,
                            alt: eventTitle,
                            type: "image/jpeg",
                        },
                    ],
                }}
                twitter={{
                    handle: "@passtickets",
                    site: "@passtickets",
                    cardType: "summary_large_image",
                }}
                additionalMetaTags={[
                    {
                        property: "og:image",
                        content: imageUrl,
                    },
                    {
                        name: "twitter:image",
                        content: imageUrl,
                    },
                    {
                        name: "keywords",
                        content: keywords,
                    },
                    {
                        name: "author",
                        content: isAr ? "باس تيكيتس" : "PassTickets",
                    },
                ]}
            />

            {/* Breadcrumb JSON-LD */}
            <BreadcrumbJsonLd
                useAppDir={false}
                itemListElements={[
                    {
                        position: 1,
                        name: isAr ? "الصفحة الرئيسية" : "Home",
                        item: `${baseUrl}${localePrefix || "/"}`,
                    },
                    {
                        position: 2,
                        name: isAr ? "الفعاليات" : "Events",
                        item: `${baseUrl}${localePrefix}/events`,
                    },
                    {
                        position: 3,
                        name: eventTitle || pageTitle,
                        item: canonicalUrl,
                    },
                ]}
            />

            {/* Event JSON-LD Structured Data */}
            <EventJsonLd
                useAppDir={false}
                url={canonicalUrl}
                name={eventTitle || ""}
                description={shortDescription}
                images={[imageUrl]}
                startDate={startDate}
                endDate={endDate}
                location={{
                    name: updatedEvent?.organisation?.en_name || "Saudi Arabia",
                    address: {
                        addressLocality:
                            updatedEvent?.address?.city || "Saudi Arabia",
                    },
                }}
                offers={{
                    url: canonicalUrl,
                    priceCurrency: "SAR",
                    availability:
                        new Date() <
                            new Date(updatedEvent?.event_date?.ending_date || "")
                            ? "https://schema.org/InStock"
                            : "https://schema.org/SoldOut",
                }}
                organizer={{
                    name: isAr
                        ? event?.organisation?.ar_name
                        : event?.organisation?.en_name,
                    url: baseUrl,
                }}
            />

            <BlurView img={isDesktop ? bgURLDesktop : bgURLMobile} />
                <EventLayout logo={event?.organisation?.logo}>
                    <div ref={contentRef} className="min-h-screen layout  md:max-w-[1220px] ">
                        <div className="flex flex-col md:flex-row max-w-7xl mx-auto  gap-10">
                            {/* Left Sidebar */}
                            <aside className="hidden md:flex flex-col w-full md:w-1/3 lg:w-[30%] sticky top-24 self-start  gap-4 pb-4">
                                {/* Poster Image */}
                                <div className="relative w-full  mx-auto flex-1 min-h-0 aspect-[11/16] rounded-[15px] overflow-hidden">
                                    <Image
                                        src={bgURLMobile}
                                        fill
                                        alt={eventTitle || "Event poster"}
                                        className="object-cover aspect-[11/16] bg-red-50 "
                                        priority
                                        sizes="(max-width: 768px) 100vw, 33vw"
                                    />
                                </div>

                                {/* Buttons Container */}
                                <div className="flex flex-col gap-2">
                                    <BuyBtn event={event} />
                                    <div className="w-full flex flex-row gap-2">
                                        <ShareAndLike
                                            setShowAuthModal={setShowAuthModal}
                                            showTitles
                                            event={event}
                                            iconsSize={20}
                                        />
                                    </div>
                                </div>
                            </aside>

                            {/* Right Main Content */}
                            <section className="w-full md:w-2/3 space-y-6  mt-24 lg:w-[70%] ">
                                <section className="flex flex-col gap-10 lg:gap-20 ">
                                    <div
                                        className={`w-full h-[1px] bg-[#FFFFFF80] hidden md:flex`}
                                    />

                                    <div className="flex flex-col gap-4">
                                        <div className="flex flex-col">
                                            <div className="w-full  aspect-[11/16] md:hidden relative">
                                                <Image
                                                    src={bgURLMobile}
                                                    fill
                                                    alt={
                                                        eventTitle ||
                                                        "Event poster"
                                                    }
                                                    className="  aspect-[11/16] object-cover rounded-[15px]"
                                                    priority
                                                    sizes="100vw"
                                                />
                                            </div>
                                            <div className="w-full flex flex-row  gap-2 md:hidden my-[14px] justify-end relative">
                                                <ShareAndLike
                                                    setShowAuthModal={
                                                        setShowAuthModal
                                                    }
                                                    event={event}
                                                    iconsSize={25}
                                                />
                                            </div>
                                            <Link
                                                href={`/organizer/${event?.organisation?._id}`}
                                            >
                                                <span className="text-[#D9D9D9] text-[20px] md:text-[20px] underline">
                                                    {isAr
                                                        ? event?.organisation
                                                            ?.ar_name
                                                        : event?.organisation
                                                            ?.en_name}
                                                </span>
                                            </Link>
                                            <span className="font-bold text-white text-[28px] md:text-[49px]">
                                                {eventTitle}
                                            </span>
                                            <span className=" text-white md:text-[20px] text-[14px]">
                                                {isAr
                                                    ? event?.ar_description
                                                    : event?.en_description}
                                            </span>
                                        </div>

                                        <div className="flex-col rounded-lg flex  gap-2 ">
                                            {/* <div className={`w-full h-[1px] bg-[#FFFFFF80]`} /> */}
                                            <div
                                                onClick={() =>
                                                    window.open(
                                                        `https://maps.google.com/?q=${event?.address?.location?.lat},${event?.address?.location?.long}`,
                                                        "_blank" // opens in a new tab
                                                    )
                                                }
                                                className="flex flex-row gap-1.5 items-center cursor-pointer hover:underline w-fit"
                                            >
                                                <div className="bg-[#00000042] rounded-md p-2">
                                                    <LocationIcon />
                                                </div>

                                                <span className="text-[#83EDBF] md:text-[16px] text-[14px] capitalize">
                                                    {t(event?.address?.city)},{" "}
                                                    {t(event?.address?.country)}
                                                </span>
                                            </div>

                                            <div className="flex flex-row gap-1.5 items-center">
                                                <div className="bg-[#00000042]  rounded-md  p-2">
                                                    <DateIcon className="fill-secondary-theme-200 dark:fill-[#83EDBF]" />
                                                </div>
                                                <span className=" text-[#83EDBF] md:text-[16px] text-[14px]">
                                                    {(() => {
                                                        const start = dayjs(eventDate)
                                                        const end = event?.event_date?.ending_date ? dayjs(event.event_date.ending_date) : null

                                                        // Effective end date: if end time is before noon, treat as previous day
                                                        const effectiveEnd = end ? (end.hour() < 12 ? end.subtract(1, "day") : end) : null
                                                        const isSameDay = effectiveEnd ? start.format("YYYY-MM-DD") === effectiveEnd.format("YYYY-MM-DD") : true

                                                        const startDateLabel = `${t(start.format("dddd"))}, ${t(start.format("MMMM"))} ${start.format("DD")}`
                                                        const endDateLabel = effectiveEnd ? `${t(effectiveEnd.format("dddd"))}, ${t(effectiveEnd.format("MMMM"))} ${effectiveEnd.format("DD")}` : ""

                                                        const startTime = `${start.format("hh")}:${start.format("mm")} ${t(start.format("A"))}`
                                                        const endTime = end ? `${end.format("hh")}:${end.format("mm")} ${t(end.format("A"))}` : ""

                                                        return (
                                                            <>
                                                                {isSameDay ? startDateLabel : `${startDateLabel} - ${endDateLabel}`}
                                                                {" "}{t("at")}{" "}
                                                                <bdi dir="ltr">{startTime}</bdi>
                                                                {endTime && <> - <bdi dir="ltr">{endTime}</bdi></>}
                                                            </>
                                                        )
                                                    })()}
                                                </span>
                                            </div>

                                            {/** Keywords */}

                                            {event?.keywords &&
                                                event?.keywords.length > 0 && (
                                                    <div className="flex flex-row mt-4 gap-2">
                                                        {event?.keywords.map(
                                                            (keyword) => (
                                                                <span
                                                                    key={
                                                                        keyword
                                                                    }
                                                                    className="px-6  h-[25px] bg-[#485e7bc6] rounded-full flex items-center justify-center text-[14px] border-[1px] border-[#fffs]"
                                                                >
                                                                    {keyword}
                                                                </span>
                                                            )
                                                        )}
                                                    </div>
                                                )}
                                        </div>
                                    </div>

                                    <div
                                        className={`w-full h-[1px] bg-[#FFFFFF80]`}
                                    />
                                </section>

                                <section className="flex flex-col gap-10">
                                    {!event?.details_sections?.length && (
                                        <div className="lg:w-[100%] flex-col flex gap-2">
                                            <span className="font-bold text-[#fff] text-[28px]">
                                                {t("description")}
                                            </span>
                                            <EventDescription event={event} />
                                        </div>
                                    )}

                                    {event?.details_sections?.length &&
                                        event?.details_sections?.map(
                                            (item, key) => {
                                                return (
                                                    <div
                                                        className="lg:w-[100%] flex-col flex gap-2"
                                                        key={key}
                                                    >
                                                        <span className="font-bold text-[#fff] text-[28px]">
                                                            {isAr
                                                                ? item?.ar_title
                                                                : item?.en_title}
                                                        </span>
                                                        <div
                                                            className="mb-6 last:mb-0"
                                                            id="event-description"
                                                            key={key}
                                                        >
                                                            <CustomMarkdown
                                                                title={""}
                                                                content={
                                                                    isAr
                                                                        ? item?.ar_content
                                                                        : item?.en_content ||
                                                                        ""
                                                                }
                                                                className="text-white text-[14px] md:text-[20px] leading-relaxed"
                                                            />
                                                        </div>
                                                    </div>
                                                )
                                            }
                                        )}

                                    {event?.artists && event.artists.length > 0 && (
                                        <div className="lg:w-[100%] flex-col flex gap-3">
                                            <span className="font-bold text-[#fff] text-[28px]">
                                                {t("artists")}
                                            </span>
                                            <div className="flex flex-wrap gap-4">
                                                {event.artists.map((artist) => (
                                                    <Link
                                                        key={artist._id}
                                                        href={`/performer/${artist._id}`}
                                                        className="flex flex-col items-center gap-2"
                                                    >
                                                        <div className="relative w-[80px] h-[80px] md:w-[100px] md:h-[100px] rounded-full overflow-hidden">
                                                            {artist.photo ? (
                                                                <Image
                                                                    src={getArtistPhoto(artist.photo)}
                                                                    fill
                                                                    alt={isAr ? artist.ar_name : artist.en_name}
                                                                    className="object-cover"
                                                                    sizes="100px"
                                                                />
                                                            ) : (
                                                                <div className="w-full h-full bg-[#485e7bc6] flex items-center justify-center text-white text-[24px] font-bold">
                                                                    {(isAr ? artist.ar_name : artist.en_name)?.charAt(0)}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <span className="text-white text-[14px] md:text-[16px] text-center max-w-[100px] truncate">
                                                            {isAr ? artist.ar_name : artist.en_name}
                                                        </span>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {updatedEvent?.media?.posters?.gallery &&
                                        updatedEvent.media.posters.gallery
                                            .length > 0 && (
                                            <div className="lg:w-[100%] flex-col flex gap-2">
                                                <span className="font-bold text-[#fff] text-[28px]">
                                                    {t("Gallery")}
                                                </span>
                                                <EventGallery event={event} />
                                            </div>
                                        )}

                                    {mediaVideos.length > 0 && (
                                        <div className="lg:w-[100%] flex-col flex gap-2">
                                            <span className="font-bold text-[#fff] text-[28px] capitalize">
                                                {t("videos")}
                                            </span>
                                            <RenderVideo
                                                url={mediaVideos[0]}
                                                controls={true}
                                            />
                                        </div>
                                    )}

                                    {mediaStories.length > 0 && (
                                        <div className="lg:w-[100%] flex-col flex gap-3">
                                            <span className="font-bold text-[#fff] text-[28px] capitalize">
                                                {t("stories")}
                                            </span>
                                            <div className="flex flex-wrap gap-3">
                                                {mediaStories.map((storyUrl: string, index: number) => (
                                                    <RenderVideo
                                                        key={`${storyUrl}-${index}`}
                                                        url={storyUrl}
                                                        controls={true}
                                                        variant="story"
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {!isLoading && (
                                        <div className="lg:w-[100%] flex-col flex gap-2">
                                            <span className="font-bold text-[#fff] text-[28px] capitalize">
                                                {t("location")}
                                            </span>
                                            <LiveMap
                                                title={eventTitle}
                                                longitude={
                                                    event?.address?.location
                                                        ?.long || 0
                                                }
                                                latitude={
                                                    event?.address?.location
                                                        ?.lat || 0
                                                }
                                            />
                                            <div>
                                                <span
                                                    className="underline cursor-pointer text-3 hover:opacity-50"
                                                    onClick={() =>
                                                        window.open(
                                                            `https://maps.google.com/?q=${event?.address?.location?.lat},${event?.address?.location?.long}`,
                                                            "_blank" // opens in a new tab
                                                        )
                                                    }
                                                >
                                                    {t("get_the_directions")}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    <div className="lg:w-[100%] flex-col flex gap-2">
                                        <span className="font-bold text-[#fff] text-[28px]">
                                            {t("USER_TAC_TITLE")}
                                        </span>
                                        <EventTerms event={event} />
                                    </div>

                                    <div className="flex md:hidden pb-4" />
                                </section>
                            </section>
                        </div>
                        <div className="hidden md:flex w-full relative  mt-20 mb-20 flex flex-col gap-4">
                            <div className="w-full lg:max-w-[1230px] aspect-video relative">
                                <Image
                                    src={bgURLDesktop}
                                    fill
                                    alt=""
                                    className=" aspect-video object-cover rounded-[15px]"
                                />
                            </div>

                            <BuyBtn event={event} />
                        </div>

                        {/* Mobile Buy Button - Fixed at viewport bottom, locks when reaching footer */}
                        <div
                            className={`w-full h-[100px] z-50 flex items-center justify-center md:hidden transition-opacity duration-300 ${showBuy ? "opacity-100" : "opacity-0 pointer-events-none"
                                } ${isLockedAtFooter ? "" : "fixed bottom-0 left-0 right-0"}`}
                        >
                            <div className="w-[90%] h-full">
                                <BuyBtn event={event} />
                            </div>
                        </div>
                    </div>
                </EventLayout>
            {showAuthModal && (
                <AuthModal
                    onClose={() => setShowAuthModal((prev) => !prev)}
                    onLoginSuccess={() => false}
                    onSignupSuccess={() => false}
                />
            )}
        </>
    )
}
